import React from 'react';
import { motion } from 'framer-motion';
import { ShaderMesh } from './ShaderMesh';
/**
 * "Horizon Beams", shader edition — inspired by Amplemarket's hero shader.
 *
 * An animated WebGL mesh-gradient (ShaderMesh) fills the whole layer, then is
 * MASKED into two curved funnel shapes pinned to the left/right edges that
 * converge to focal points at the sides of the prompt input.
 *
 * CRITICAL FIX: The mask paths now have a heavy SVG `feGaussianBlur` applied to
 * them. This turns the hard, solid geometric shapes into soft, glowing, blurred
 * "flames" or "plumes" of color, exactly matching the Amplemarket reference.
 */
// Hourglass / bow-tie focal region (0..1 coords). Rather than pinching to a
// single point ("dot") in the middle, the two plumes meet at a RECTANGULAR
// WAIST — a short vertical band around the center of the prompt box (~56% down).
const FY = 0.56;
// Horizontal extent of the neck (the rectangle's left/right edges).
const NX_L = 0.46;
const NX_R = 0.54;
// Vertical extent of the neck (the rectangle's top/bottom edges). Kept short
// and centered on FY so the waist reads as a small, rounded pinch rather than a
// tall rectangle.
const NY_T = 0.535;
const NY_B = 0.585;
// Each plume has a straight VERTICAL inner edge (the L command) — that flat
// side is what turns the focal point from a dot into a rectangle. The concave
// top/bottom walls fan out to the screen edges.
const LEFT_FUNNEL = `M 0 0.05 C 0.15 0.28, 0.30 0.42, ${NX_L} ${NY_T} L ${NX_L} ${NY_B} C 0.30 0.70, 0.15 0.84, 0 1.0 Z`;
const RIGHT_FUNNEL = `M 1 0.05 C 0.85 0.28, 0.70 0.42, ${NX_R} ${NY_T} L ${NX_R} ${NY_B} C 0.70 0.70, 0.85 0.84, 1 1.0 Z`;
// A connecting band so the two plumes read as one continuous rectangular waist.
const NECK_X = 0.44;
const NECK_W = 0.12;
export function FloatingCanvas() {
  return <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true" data-id="element-3">
      {/* SVG mask definition — objectBoundingBox so it scales with the box */}
      <svg width="0" height="0" className="absolute" aria-hidden="true" data-id="element-4">
        <defs data-id="element-5">
          {/* A light blur keeps the funnel edges soft but crisp — the white page
           behind provides clean separation between the two plumes. */}
          <filter id="funnelBlur" x="-50%" y="-50%" width="200%" height="200%" filterUnits="objectBoundingBox" data-id="element-6">
            <feGaussianBlur stdDeviation="0.012" data-id="element-7" />
          </filter>
          <mask id="funnelMask" maskContentUnits="objectBoundingBox" data-id="element-8">
            <path d={LEFT_FUNNEL} fill="white" filter="url(#funnelBlur)" data-id="element-9" />
            <path d={RIGHT_FUNNEL} fill="white" filter="url(#funnelBlur)" data-id="element-10" />
            {/* Connects the two plumes into one continuous rectangular waist */}
            <rect x={NECK_X} y={NY_T} width={NECK_W} height={NY_B - NY_T} fill="white" filter="url(#funnelBlur)" data-id="element-11" />
          </mask>
        </defs>
      </svg>

      {/* Masked, animated funnels */}
      <div className="absolute inset-0" style={{
      WebkitMaskImage: 'url(#funnelMask)',
      maskImage: 'url(#funnelMask)',
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat'
    }} data-id="element-12">
        {/* Living shader gradient */}
        <ShaderMesh className="absolute inset-0 w-full h-full" style={{
        opacity: 0.95
      }} data-id="element-13" />
      </div>

      {/* Bright breathing "intake" glows at the input's sides, on top */}
      <motion.div className="absolute rounded-full" style={{
      left: `${NX_L * 100}%`,
      top: `${FY * 100}%`,
      width: 160,
      height: 160,
      transform: 'translate(-50%, -50%)',
      background: 'radial-gradient(circle, rgba(110,231,183,0.4), transparent 70%)',
      filter: 'blur(12px)'
    }} animate={{
      scale: [1, 1.2, 1],
      opacity: [0.6, 0.9, 0.6]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-14" />
      <motion.div className="absolute rounded-full" style={{
      left: `${NX_R * 100}%`,
      top: `${FY * 100}%`,
      width: 160,
      height: 160,
      transform: 'translate(-50%, -50%)',
      background: 'radial-gradient(circle, rgba(196,181,253,0.4), transparent 70%)',
      filter: 'blur(12px)'
    }} animate={{
      scale: [1, 1.2, 1],
      opacity: [0.6, 0.9, 0.6]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} data-id="element-15" />
    </div>;
}